import type { SxProps, Theme } from "@mui/material/styles";
import { gridClasses } from "@mui/x-data-grid";

/** Shared DataGrid styling aligned with Regulations Next homepage table. */
export function getRegulationsListDataGridSx(
  primaryColor: string,
): SxProps<Theme> {
  return {
    flex: 1,
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    "--DataGrid-t-header-background-base": "#f9fafb",
    "--DataGrid-rowBorderColor": "transparent",
    "&.MuiDataGrid-root": {
      border: "none",
      outline: "none",
      boxShadow: "none",
    },
    "& .MuiDataGrid-scrollShadow, & .MuiDataGrid-scrollShadow--horizontal, & .MuiDataGrid-scrollShadow--vertical":
      {
        display: "none !important",
      },
    "& .MuiDataGrid-main": {
      position: "relative",
      borderLeft: "none !important",
      borderRight: "none !important",
      outline: "none",
    },
    "& .MuiDataGrid-main::after": {
      content: '""',
      position: "absolute",
      top: "calc(var(--DataGrid-headersTotalHeight) - var(--DataGrid-columnHeaderHeight))",
      right: 0,
      width: "var(--DataGrid-scrollbarSize, 17px)",
      height: "var(--DataGrid-columnHeaderHeight)",
      backgroundColor: "#f9fafb",
      pointerEvents: "none",
      zIndex: 10,
    },
    "& .MuiDataGrid-pinnedColumns--header": {
      borderLeft: "none !important",
      borderRight: "none !important",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-columnSeparator": {
      color: "transparent",
      "&:hover": {
        color: "#c0c0c0",
      },
    },
    "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator": {
      color: "#c0c0c0",
    },
    "& .MuiDataGrid-columnSeparator--resizing": {
      color: primaryColor,
    },
    "& .MuiDataGrid-columnHeader--dragging": {
      backgroundColor: "#f3f4f6",
    },
    "& .MuiDataGrid-topContainer": {
      borderLeft: "none !important",
      borderRight: "none !important",
      borderBottom: "none",
      boxShadow: "none !important",
    },
    "& .MuiDataGrid-topContainer::after": {
      display: "none",
    },
    "& .MuiDataGrid-toolbarContainer, & .MuiDataGrid-toolbar": {
      borderBottom: "none !important",
    },
    "& .MuiDataGrid-columnHeaders": {
      fontSize: "14px",
      fontWeight: 600,
      textTransform: "capitalize",
      color: "#262626",
      backgroundColor: "#f9fafb",
      borderLeft: "none !important",
      borderRight: "none !important",
      borderTop: "none !important",
      borderBottom: "none !important",
    },
    "& .MuiDataGrid-columnHeaders [role='row']": {
      backgroundColor: "#f9fafb",
    },
    "& .MuiDataGrid-columnHeadersInner": {
      backgroundColor: "#f9fafb",
      borderLeft: "none !important",
      borderRight: "none !important",
    },
    "& .MuiDataGrid-columnHeadersInner, & .MuiDataGrid-columnHeader--filler": {
      backgroundColor: "#f9fafb !important",
    },
    "& .MuiDataGrid-topContainer .MuiDataGrid-filler, & .MuiDataGrid-topContainer .MuiDataGrid-scrollbarFiller":
      {
        backgroundColor: "#f9fafb !important",
      },
    "& .MuiDataGrid-scrollbarFiller--header, & .MuiDataGrid-scrollbarFiller--borderTop, & .MuiDataGrid-filler--header, & .MuiDataGrid-scrollbarFiller--pinnedRight":
      {
        backgroundColor: "#f9fafb !important",
      },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: "#ffffff",
    },
    "& .MuiDataGrid-virtualScroller > .MuiDataGrid-filler": {
      backgroundColor: "#ffffff !important",
    },
    "& .MuiDataGrid-virtualScroller > .MuiDataGrid-filler .MuiDataGrid-filler--pinnedRight, & .MuiDataGrid-virtualScroller > .MuiDataGrid-filler .MuiDataGrid-filler--pinnedLeft":
      {
        backgroundColor: "#ffffff !important",
      },
    "& .MuiDataGrid-filler--borderBottom, & .MuiDataGrid-scrollbarFiller--borderBottom":
      {
        borderBottom: "none !important",
      },
    "& .MuiDataGrid-columnHeader": {
      backgroundColor: "#f9fafb",
      borderRight: "none !important",
      borderLeft: "none !important",
      borderBottom: "none !important",
    },
    "& .MuiDataGrid-columnHeader--pinnedRight, & .MuiDataGrid-columnHeader--pinnedLeft":
      {
        backgroundColor: "#f9fafb",
        borderLeft: "none !important",
        borderRight: "none !important",
        boxShadow: "none",
      },
    "& .MuiDataGrid-pinnedColumnHeaders": {
      backgroundColor: "#f9fafb",
      borderLeft: "none !important",
      borderRight: "none !important",
      boxShadow: "none",
    },
    "& .MuiDataGrid-cell": {
      fontSize: "14px",
      color: "#262626",
      fontWeight: 500,
      borderRight: "none !important",
    },
    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
      outline: "none",
    },
    [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
      {
        outline: "none",
      },
    "& .MuiDataGrid-cell--pinnedRight": {
      borderLeft: "none !important",
      boxShadow: "none",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 600,
      color: "#262626",
      fontSize: "14px",
    },
    "& .ai-plan-description-cell": {
      display: "flex !important",
      alignItems: "center !important",
      overflow: "hidden",
      "& .MuiDataGrid-cellContent": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        lineHeight: 1.4,
        width: "100%",
        minWidth: 0,
      },
    },
  };
}

/**
 * Empty grid overlay positioning — mirrors RegulationsTable.module.css `.dataGridPanelEmpty`.
 * MUI overlayWrapper is 0×0 by default; anchor it to fill the body below headers.
 */
export function getDataGridEmptyStateSx(): SxProps<Theme> {
  return {
    "&.MuiDataGrid-root": {
      "--DataGrid-hasScrollY": 0,
    },
    "& .MuiDataGrid-virtualScroller": {
      position: "relative",
    },
    "& .MuiDataGrid-overlayWrapper": {
      position: "absolute !important",
      top: "var(--DataGrid-headersTotalHeight) !important",
      left: "0 !important",
      right: "0 !important",
      bottom: "0 !important",
      width: "auto !important",
      height: "auto !important",
      zIndex: 4,
    },
    "& .MuiDataGrid-overlayWrapperInner": {
      width: "100% !important",
      height: "100% !important",
    },
    "& .MuiDataGrid-virtualScrollerContent, & .MuiDataGrid-filler": {
      display: "none !important",
    },
    "& .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-scrollbarFiller--pinnedRight":
      {
        display: "none !important",
      },
    "& .MuiDataGrid-main::after": {
      display: "none !important",
    },
  };
}

export const regulationsListDataGridPanelSx: SxProps<Theme> = {
  width: "100%",
  boxSizing: "border-box",
  bgcolor: "background.paper",
  borderRadius: 1,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  boxShadow: "none",
  overflow: "hidden",
  border: "1px solid #e5e7eb",
};

/** AI Plan Assessment Plan table — no outer border; white internal horizontal inset. */
export const aiPlanDataGridPanelSx: SxProps<Theme> = {
  width: "100%",
  boxSizing: "border-box",
  bgcolor: "background.paper",
  borderRadius: 0,
  boxShadow: "none",
  overflow: "hidden",
  border: "none",
  px: "24px",
};

/**
 * AiHub result list tables — same horizontal row/header dividers as Object Analysis
 * detail group tables (`#E7ECFC` under header, `#F2F2F2` between rows).
 * Compose after `getRegulationsListDataGridSx`.
 */
export const aiHubListDataGridRowBorderSx: SxProps<Theme> = {
  // Row separators are drawn by the grid itself as `cell { border-top }`;
  // only the color needs restoring, adding cell borders would double the line.
  "--DataGrid-rowBorderColor": "#F2F2F2",
  "& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader, & .MuiDataGrid-row--borderBottom .MuiDataGrid-filler, & .MuiDataGrid-row--borderBottom .MuiDataGrid-scrollbarFiller":
    {
      borderBottom: "1px solid #E7ECFC !important",
    },
};
